<template>
  <v-container v-if="!!file">
    <toolbar mode="file" :item="file" />
    <v-card flat>
      <v-tabs v-model="tab">
        <v-tabs-slider></v-tabs-slider>
        <v-tab key="compute" href="#compute">Compute</v-tab>
        <v-tab key="rename" href="#rename">Rename</v-tab>
        <v-tab key="drop" href="#drop">Drop</v-tab>
        <v-tab key="clean" href="#clean">Clean</v-tab>
        <v-tab key="holdback" href="#holdback">Holdback</v-tab>
      </v-tabs>
      <v-divider/>
      <v-tabs-items v-model="tab">
        <v-tab-item value="compute">
          <v-row>
            <v-col cols="12" md="8" lg="6" xl="4">
              <v-form ref="compute" v-model="valid.compute" method="patch" @submit.prevent="onCompute">
                <v-card flat tile>
                  <v-card-title>Compute date</v-card-title>
                  <v-card-subtitle>Compute a date column from Year and Month columns</v-card-subtitle>
                  <v-card-text>
                    <v-autocomplete
                      v-model="date.year"
                      :rules="rules"
                      :items="columns"
                      label="Year"
                      required
                    ></v-autocomplete>
                    <v-autocomplete
                      v-model="date.month"
                      :rules="rules"
                      :items="columns"
                      label="Month"
                      required
                    ></v-autocomplete>
                  </v-card-text>
                  <v-card-actions>
                    <v-btn depressed color="success" type="submit" :disabled="!valid.compute">Compute</v-btn>
                    <v-btn text @click="$router.go(-1)">Cancel</v-btn>
                  </v-card-actions>
                </v-card>
              </v-form>
            </v-col>
          </v-row>
        </v-tab-item>
        <v-tab-item value="rename">
          <v-row>
            <v-col cols="12" md="8" lg="6" xl="4">
              <v-form ref="rename" v-model="valid.rename" method="patch" @submit.prevent="onRename">
                <v-card flat tile>
                  <v-card-title>Rename column</v-card-title>
                  <v-card-subtitle>Change the title of a data column</v-card-subtitle>
                  <v-card-text>
                    <v-autocomplete
                      v-model="rename.old"
                      :rules="rules"
                      :items="columns"
                      label="Old name"
                      required
                    ></v-autocomplete>
                    <v-text-field v-model="rename.new" :rules="rules" label="New name" required></v-text-field>
                  </v-card-text>
                  <v-card-actions>
                    <v-btn depressed color="success" type="submit" :disabled="!valid.rename">Rename</v-btn>
                    <v-btn text @click="$router.go(-1)">Cancel</v-btn>
                  </v-card-actions>
                </v-card>
              </v-form>
            </v-col>
          </v-row>
        </v-tab-item>
        <v-tab-item value="drop">
          <v-row>
            <v-col cols="12" md="8" lg="6" xl="4">
              <v-form ref="drop" v-model="valid.drop" method="patch" @submit.prevent="onDrop">
                <v-card flat tile>
                  <v-card-title>Drop column</v-card-title>
                  <v-card-subtitle>Remove a column from the data frame</v-card-subtitle>
                  <v-card-text>
                    <v-autocomplete
                      v-model="drop"
                      multiple
                      :rules="rules"
                      :items="columns"
                      label="Select any number of column(s) you want to remove"
                      required
                    ></v-autocomplete>
                  </v-card-text>
                  <v-card-actions>
                    <v-btn depressed color="error" type="submit" :disabled="!valid.drop">Drop</v-btn>
                    <v-btn text @click="$router.go(-1)">Cancel</v-btn>
                  </v-card-actions>
                </v-card>
              </v-form>
            </v-col>
          </v-row>
        </v-tab-item>
        <v-tab-item value="clean">
          <v-row>
            <v-col cols="12" md="8" lg="6" xl="4">
              <v-form ref="clean" v-model="valid.clean" method="patch" @submit.prevent="onClean">
                <v-card flat tile>
                  <v-card-title>Clean data</v-card-title>
                  <v-card-subtitle>Cleans data by removing junk values and outliers.</v-card-subtitle>
                  <v-card-text>
                    <v-autocomplete
                      v-model="clean.columns"
                      multiple
                      :rules="rules"
                      :items="columns"
                      label="Select any number of column(s) you want to clean"
                      required
                    ></v-autocomplete>

                    <v-checkbox
                      v-model="clean.duplicates"
                      hide-details
                      label="Remove duplicate records using column(s) as unique identifiers"
                    ></v-checkbox>

                    <v-checkbox
                      v-model="clean.empty"
                      hide-details
                      label="Remove rows/columns that are entirely blank or empty"
                    ></v-checkbox>

                    <v-checkbox v-model="clean.nulls" hide-details label="Replace null values with 0's"></v-checkbox>
                  </v-card-text>
                  <v-card-actions>
                    <v-btn depressed color="success" type="submit" :disabled="!valid.clean">Clean</v-btn>
                    <v-btn text @click="$router.go(-1)">Cancel</v-btn>
                  </v-card-actions>
                </v-card>
              </v-form>
            </v-col>
          </v-row>
        </v-tab-item>
        <v-tab-item value="holdback">
          <v-row>
            <v-col cols="12" md="8" lg="6" xl="4">
              <v-form ref="holdback" v-model="valid.holdback" method="patch" @submit.prevent="onHoldback">
                <v-card flat tile>
                  <v-card-title>Holdback data</v-card-title>
                  <v-card-subtitle>
                    Removes some months of production data and creates a new file for holdback analysis.
                  </v-card-subtitle>
                  <v-card-text>
                    <v-slider
                      :label="`Holdback period (${holdback.months} months)`"
                      v-model.number="holdback.months"
                      min="1"
                      max="12"
                      suffix="months"
                      disabled
                      hint="Wells with sufficient production data will be retained in the final output."
                    />
                    <v-autocomplete
                      v-model="holdback.api"
                      :rules="rules"
                      :items="columns"
                      label="Unique well identifier"
                      required
                    ></v-autocomplete>
                    <v-autocomplete
                      v-model="holdback.date"
                      :rules="rules"
                      :items="columns"
                      label="Production date"
                      required
                    ></v-autocomplete>
                    <v-autocomplete
                      v-model="holdback.targets"
                      :rules="rules"
                      :items="columns"
                      label="Target(s)"
                      required
                      multiple
                    ></v-autocomplete>
                  </v-card-text>
                  <v-card-actions>
                    <v-btn depressed color="success" type="submit" :disabled="!valid.holdback">Holdback</v-btn>
                    <v-btn text @click="$router.go(-1)">Cancel</v-btn>
                  </v-card-actions>
                </v-card>
              </v-form>
            </v-col>
          </v-row>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </v-container>
</template>
<script>
import toolbar from "@/components/toolbar"
import Files from "@/api/files"
export default {
  name: "EditFile",
  components: { toolbar },
  data() {
    return {
      tab: null,
      file: null,
      columns: [],
      rules: [v => !!v || "Field is required"],
      valid: {
        compute: false,
        rename: false,
        drop: false,
        clean: false,
        holdback: false
      },
      date: {
        year: null,
        month: null
      },
      rename: {
        old: null,
        new: null
      },
      drop: [],
      clean: {
        columns: [],
        singles: false,
        empty: false,
        nulls: false,
        duplicates: false
      },
      holdback: {
        months: 12,
        api: null,
        date: null,
        targets: []
      }
    }
  },
  created() {
    Files.retrieve(this.$route.params.uuid).then(file => this.file = file.data)
    Files.columns(this.$route.params.uuid).then(columns => this.columns = columns.data)
  },
  methods: {
    onCompute() {
      Files.compute(this.$route.params.uuid, this.date).then(response => {
        this.notify(response.data)
        this.$router.push({ name: "fileShow", params: { uuid: this.$route.params.uuid } })
      })
    },
    onRename() {
      Files.rename(this.$route.params.uuid, this.rename).then(response => {
        this.notify(response.data)
        this.$router.push({ name: "fileShow", params: { uuid: this.$route.params.uuid } })
      })
    },
    onDrop() {
      Files.drop(this.$route.params.uuid, { columns: this.drop }).then(response => {
        this.notify(response.data)
        this.$router.push({ name: "fileShow", params: { uuid: this.$route.params.uuid } })
      })
    },
    onClean() {
      Files.clean(this.$route.params.uuid, { config: this.clean }).then(response => {
        this.notify(response.data)
        this.$router.push({ name: "fileShow", params: { uuid: this.$route.params.uuid } })
      })
    },
    onHoldback() {
      Files.holdback(this.$route.params.uuid, this.holdback).then(response => {
        this.notify(response.data)
        this.$router.push({ name: "fileList" })
      })
    }
  }
}
</script>
